import React, { useState, useEffect } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import { WorkbookImagesTab } from '@/components/content-generator/editor/tabs/WorkbookImagesTab';
import { ProjectOutletContext } from '@/types/project';

// Empty state imports
import { EmptyStateLayout } from '@/components/shared/empty-states/EmptyStateLayout';
import { ProjectImportImagesEmptyState } from '@/components/shared/import';
import { Images } from 'lucide-react';

// Import the hook to check if images exist
import { usePrivateImageLibrary } from '@/hooks/content-generator/private-image-library/use-private-image-library';

/**
 * ProjectImagesPage - Images route for project system
 * Contains private image library functionality
 */
const ProjectImagesPage: React.FC = () => {
  const { project, goal, agentThreadId, navigationData, registerImageCreationHandler } = useOutletContext<ProjectOutletContext>();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Modal state for image upload/generation
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
  const [autoOpenImageId, setAutoOpenImageId] = useState<string | null>(null);
  
  if (!project) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600 dark:text-gray-300">
          Project not found.
        </p>
      </div>
    );
  }
  
  // Get image data to determine empty state
  const { data: imageData, isLoading: isImagesLoading } = usePrivateImageLibrary(project.id);
  const allImages = imageData?.pages.flatMap(page => page.data) || [];
  
  // Empty state detection logic
  const shouldShowCenteredLayout = !isImagesLoading && allImages.length === 0;
  
  const handleCreateImage = () => {
    setIsUploadModalOpen(true);
  };
  
  // Check for openImage URL parameter and auto-open image lightbox
  useEffect(() => {
    const openImageId = searchParams.get('openImage');
    if (openImageId) {
      console.log('[ProjectImagesPage] Auto-opening image lightbox from URL parameter:', openImageId);
      
      // Store the image ID to pass to WorkbookImagesTab
      setAutoOpenImageId(openImageId);
      
      // Remove the parameter from URL to clean it up
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('openImage');
      setSearchParams(newSearchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);
  
  // Register image creation handler with the layout
  useEffect(() => {
    if (registerImageCreationHandler) {
      registerImageCreationHandler(handleCreateImage);
    }
    
    // Cleanup: unregister when component unmounts
    return () => {
      if (registerImageCreationHandler) {
        registerImageCreationHandler(undefined);
      }
    };
  }, [registerImageCreationHandler]);
  
  const pages = navigationData?.pages || [];
  
  return shouldShowCenteredLayout ? (
    /* Empty State Layout - Import Images System */
    <EmptyStateLayout
      icon={<Images className="h-8 w-8 text-blue-500 dark:text-blue-400" />}
      title="Import Images"
      description="Organize your visual assets in this project and share them with your team"
      variant="blue"
    >
      <ProjectImportImagesEmptyState 
        projectId={project.id}
        onImportComplete={() => {
          // The page will automatically refresh when items are imported
          console.log('Images imported successfully');
        }}
      />
    </EmptyStateLayout>
  ) : (
    /* Normal Layout - Show WorkbookImagesTab */
    <WorkbookImagesTab 
      pageId={project.id}
      coverImage={project.metadata?.coverImage}
      onCoverImageChange={(url) => {
        // TODO: Implement cover image change functionality
        console.log('Cover image changed for project:', project.id, url);
      }}
      isUploadModalOpen={isUploadModalOpen}
      onCloseUploadModal={() => setIsUploadModalOpen(false)}
      onCreateImage={handleCreateImage}
      autoOpenImageId={autoOpenImageId}
      onAutoOpenComplete={() => setAutoOpenImageId(null)}
    />
  );
};

export default ProjectImagesPage; 